import omit from 'lodash-es/omit';

import { Context } from '@/shared/types/chat';

import { isSafeVariableLabel } from '../hooks/useContextVariables';

const isJsonObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

type JsonBranch = Record<string, unknown> | unknown[];

const isBranch = (value: unknown): value is JsonBranch =>
  isJsonObject(value) || Array.isArray(value);

const emptyLike = (value: JsonBranch): JsonBranch => (Array.isArray(value) ? [] : {});

const entriesOf = (value: JsonBranch): [string, unknown][] =>
  Array.isArray(value)
    ? Array.from(value, (entry, index) => [String(index), entry])
    : Object.entries(value);

const withoutCleared = (root: unknown): unknown => {
  if (!isBranch(root)) {
    return root;
  }
  const out = emptyLike(root);
  const copied: [JsonBranch, string, JsonBranch][] = [];
  const pending: [JsonBranch, JsonBranch][] = [[root, out]];

  while (pending.length > 0) {
    const [source, target] = pending.pop() as [JsonBranch, JsonBranch];
    for (const [key, entry] of entriesOf(source)) {
      if (entry === null && !Array.isArray(source)) {
        continue;
      }
      if (isBranch(entry)) {
        const branch = emptyLike(entry);
        (target as Record<string, unknown>)[key] = branch;
        copied.push([target, key, branch]);
        pending.push([entry, branch]);
      } else {
        (target as Record<string, unknown>)[key] = entry;
      }
    }
  }

  for (let index = copied.length - 1; index >= 0; index -= 1) {
    const [parent, key, branch] = copied[index];
    if (!Array.isArray(parent) && isJsonObject(branch) && Object.keys(branch).length === 0) {
      delete (parent as Record<string, unknown>)[key];
    }
  }
  return out;
};

export const toContextVariables = (context: Context | null | undefined): Record<string, unknown> =>
  withoutCleared(omit(context ?? {}, ['global', 'tags'])) as Record<string, unknown>;

export const toContextJson = (context: Context | null | undefined): string =>
  JSON.stringify({ context: toContextVariables(context) }, null, 2);

// Well under what `cloneDeep` behind the editor survives, and far past any real context.
const MAX_DEPTH = 100;

const withoutUnsafeKeys = (value: unknown, depth = 0): unknown => {
  if (depth > MAX_DEPTH) {
    throw new RangeError('Context nesting is too deep');
  }
  if (Array.isArray(value)) {
    return value.map((entry) => withoutUnsafeKeys(entry, depth + 1));
  }
  if (!isJsonObject(value)) {
    return value;
  }
  const entries = Object.entries(value)
    .filter(([key]) => isSafeVariableLabel(key))
    .map(([key, entry]) => [key, withoutUnsafeKeys(entry, depth + 1)] as const);
  return Object.fromEntries(entries);
};

export const fromContextJson = (text: string): Context | null => {
  try {
    const parsed: unknown = JSON.parse(text);
    if (!isJsonObject(parsed)) {
      return null;
    }
    const isWrapper = Object.keys(parsed).length === 1 && isJsonObject(parsed.context);
    const context = isWrapper ? parsed.context : parsed;
    if (!isJsonObject(context)) {
      return null;
    }
    return withoutUnsafeKeys(omit(context, ['global', 'tags'])) as Context;
  } catch {
    return null;
  }
};
